import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

// Live "starts in X" countdown to a festival's start date (00:00 KST of start_date).
// Renders nothing once the start moment has passed. Two variants: compact "card"
// pill and a larger "hero" set of boxes.
const useCountdown = (startDate) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const targetMs = new Date(`${startDate}T00:00:00+09:00`).getTime();
  const diff = targetMs - now;
  if (Number.isNaN(targetMs) || diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n) => String(n).padStart(2, '0');

export const Countdown = ({ startDate, variant = 'card', testid = 'countdown' }) => {
  const t = useCountdown(startDate);
  if (!t) return null;

  if (variant === 'hero') {
    const boxes = [
      { label: 'Days', v: t.d },
      { label: 'Hrs', v: t.h },
      { label: 'Min', v: t.m },
      { label: 'Sec', v: t.s },
    ];
    return (
      <div className="inline-flex items-center gap-2.5 sm:gap-3" data-testid={testid}>
        {boxes.map((b) => (
          <div
            key={b.label}
            className="flex flex-col items-center justify-center rounded-xl border border-secondary-main/25 bg-black/40 px-3 sm:px-4 py-2 min-w-[58px] sm:min-w-[68px]"
            style={{ backdropFilter: 'blur(6px)' }}
          >
            <span className="font-mono text-2xl sm:text-3xl font-bold text-white tabular-nums leading-none">{pad(b.v)}</span>
            <span className="mt-1 font-heading text-[9px] uppercase tracking-[0.25em] text-secondary-main/80">{b.label}</span>
          </div>
        ))}
      </div>
    );
  }

  // card variant — compact pill
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-secondary-main/25 bg-secondary-main/[0.06] px-3 py-1.5"
      data-testid={testid}
    >
      <Clock className="w-3.5 h-3.5 text-secondary-main shrink-0" />
      <span className="font-heading text-[10px] uppercase tracking-[0.2em] text-secondary-main/80">Starts in</span>
      <span className="font-mono text-sm text-white tabular-nums">
        {t.d}d {pad(t.h)}h {pad(t.m)}m {pad(t.s)}s
      </span>
    </div>
  );
};
